/* eslint-disable react/jsx-no-bind */
import { Settings, Close } from '@mui/icons-material';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem as DropdownItem,
  Paper,
  IconButton,
  Menu,
  CardContent,
  Card,
  TablePagination,
  Modal,
  Typography,
  useTheme,
  Divider,
  Grid,
  Chip,
  Button,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';
import logger from '../../utils/logger';

import shipmentService from './shimentService';

const ShipmentManagement = () => {
  const [apiResponse, setApiResponse] = useState({
    status: false,
    message: '',
    data: {
      result: [],
      total_records: 0,
      page: 1,
      page_size: 10,
    },
    status_code: 0,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const isFetchingRef = useRef(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'CRT', label: 'Created' },
    { value: 'ASG', label: 'Assigned' },
    { value: 'INT', label: 'In Transit' },
    { value: 'DLD', label: 'Delivered' },
    { value: 'CLD', label: 'Canceled' },
    { value: 'RFP', label: 'Ready for Pickup' },
    { value: 'ICR', label: 'Incident Reported' },
    { value: 'NTY', label: 'Notify' },
  ];

  const statusLabels = {
    CRT: 'Created',
    ASG: 'Assigned',
    PKP: 'Pickup',
    INT: 'In Transit',
    DLD: 'Delivered',
    CLD: 'Canceled',
    RFP: 'Ready for Pickup',
    ICR: 'Incident',
    NTY: 'Notify',
  };

  const shipmentData = apiResponse.data?.result || [];
  const totalRecords = apiResponse.data?.total_records || 0;

  // Pagination object extracted from API response
  const pagination = {
    total: totalRecords,
    page: apiResponse.data?.page || 1,
    pageSize: apiResponse.data?.page_size || rowsPerPage,
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'delivered':
        return 'success';
      case 'in transit':
        return 'warning';
      case 'created':
        return 'info';
      case 'canceled':
      case 'cancelled':
        return 'error';
      case 'ready for pickup':
        return 'secondary';
      case 'assigned':
        return 'primary';
      case 'unassigned':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    if (!status) return 'N/A';
    const upper = status.toUpperCase();
    if (statusLabels[upper]) return statusLabels[upper];
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const fetchShipments = useCallback(
    async (
      pageNum = 1,
      pageSize = 10,
      status = '',
      fromDateParam = null,
      toDateParam = null,
      searchParam = '',
    ) => {
      // Prevent duplicate concurrent calls
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      setLoading(true);
      try {
        const res = await shipmentService.fetchShipments(
          pageNum,
          pageSize,
          status,
          fromDateParam,
          toDateParam,
          searchParam,
        );
        setApiResponse(res.data);
      } catch (err) {
        toast.error('Error fetching shipments');
        logger.error('Error fetching shipments:', err);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [],
  );

  useEffect(() => {
    // Initial fetch on mount and when page/rowsPerPage changes
    // Filters are applied only when "Search" button is clicked
    fetchShipments(
      page + 1,
      rowsPerPage,
      statusFilter,
      fromDate,
      toDate,
      search,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchShipments(1, rowsPerPage, statusFilter, fromDate, toDate, search);
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setFromDate(null);
    setToDate(null);
    setPage(0);
    // Fetch with reset values
    fetchShipments(1, rowsPerPage, '', null, null, '');
  };

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedShipment(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedShipment(null);
  };

  const handleDelete = () => {
    if (!selectedShipment) return;

    setLoading(true);
    shipmentService
      .deleteShipment(selectedShipment.shipmentId)
      .then(() => {
        toast.success('Shipment deleted successfully');
        return fetchShipments(
          page + 1,
          rowsPerPage,
          statusFilter,
          fromDate,
          toDate,
          search,
        );
      })
      .then(() => handleMenuClose())
      .catch((error) => {
        toast.error('Error deleting shipment');
        logger.error('Error deleting shipment:', error);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          padding: 1,
          margin: 2,
          marginLeft: 4,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 4,
              paddingTop: 2,
              paddingBottom: 0,
            }}
          >
            <h3>Shipment Management</h3>
          </Box>
          <Divider />
          <Box sx={{ padding: 4 }}>
            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{ marginBottom: 3 }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Search by Shipment ID or Phone Number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={search}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  label="Filter by Status"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (selected === '' || !selected) {
                        return 'All Statuses';
                      }
                      const option = statusOptions.find(
                        (opt) => opt.value === selected,
                      );
                      return option ? option.label : selected;
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      '&.Mui-focused fieldset': {
                        borderColor: colors.swiftcarry[100],
                        borderWidth: '2px',
                      },
                      '&:hover fieldset': {
                        borderColor: colors.swiftcarry[100],
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiSelect-select': {
                      fontSize: '14px',
                      padding: '10px 14px',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'gray',
                      transform: 'translate(14px, 10px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.swiftcarry[100],
                      transform: 'translate(14px, -9px) scale(0.75)',
                    },
                  }}
                >
                  {statusOptions.map((option) => (
                    <DropdownItem key={option.value} value={option.value}>
                      {option.label}
                    </DropdownItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DatePicker
                      label="From Date"
                      value={fromDate}
                      onChange={(newDate) => {
                        setFromDate(newDate);
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'gray',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: colors.swiftcarry[100],
                            },
                          },
                        },
                      }}
                    />
                    <Typography sx={{ color: '#666', mx: 0.5 }}>—</Typography>
                    <DatePicker
                      label="To Date"
                      value={toDate}
                      onChange={(newDate) => {
                        setToDate(newDate);
                      }}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              borderRadius: '8px',
                              backgroundColor: 'white',
                              '&.Mui-focused fieldset': {
                                borderColor: colors.swiftcarry[100],
                                borderWidth: '2px',
                              },
                              '&:hover fieldset': {
                                borderColor: colors.swiftcarry[100],
                              },
                            },
                            '& .MuiInputBase-input': {
                              fontSize: '14px',
                              padding: '10px 14px',
                            },
                            '& .MuiInputLabel-root': {
                              color: 'gray',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: colors.swiftcarry[100],
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{
                      bgcolor: colors.swiftcarry[100],
                      '&:hover': {
                        bgcolor: colors.swiftcarry[100],
                      },
                      height: '40px',
                      flex: 1,
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetFilters}
                    sx={{
                      borderColor: '#ccc',
                      color: '#666',
                      height: '40px',
                      flex: 1,
                      '&:hover': {
                        borderColor: colors.swiftcarry[100],
                        color: colors.swiftcarry[100],
                        backgroundColor: `${colors.swiftcarry[100]}10`,
                      },
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <TableContainer
              component={Paper}
              sx={{ position: 'relative', minHeight: 200 }}
            >
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Shipment ID
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Source</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Destination
                    </TableCell>
                    {/* <TableCell style={{ fontWeight: "bold" }}>
                      Vehicle
                    </TableCell>*/}
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Package
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Expected Delivery
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created By
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>
                      Created On
                    </TableCell>
                    <TableCell style={{ fontWeight: 'bold' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ height: 300 }}></TableCell>
                    </TableRow>
                  ) : shipmentData.length > 0 ? (
                    shipmentData.map((row) => (
                      <TableRow key={row.shipmentId}>
                        <TableCell>{row.shipmentId}</TableCell>
                        <TableCell>
                          {row.sourceName || row.source || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {row.destName || row.dest || 'N/A'}
                        </TableCell>
                        {/* <TableCell>{row.vehicleName || "N/A"}</TableCell>*/}
                        <TableCell>
                          {row.pkgWeightName
                            ? `${row.pkgWeightName} (${
                              row.pkgTypeName || 'N/A'
                            })`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {formatToIST(row.exceptedDelivery) || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status || 'N/A'}
                            color={getStatusColor(row.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {row.createdBy ? (
                            <>
                              <div>{row.createdBy}</div>
                              {row.phoneNo && (
                                <div
                                  style={{ fontSize: '0.8rem', color: '#666' }}
                                >
                                  {row.phoneNo}
                                </div>
                              )}
                            </>
                          ) : row.phoneNo ? (
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                              {row.phoneNo}
                            </div>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          {row.createdOn ? formatToIST(row.createdOn) : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={(event) => handleMenuOpen(event, row)}
                          >
                            <Settings />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        align="center"
                        sx={{ height: 300 }}
                      >
                        No shipments found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              // Use total from API pagination so it reflects full dataset
              count={pagination.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                width: '100%',
                overflow: 'auto',
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: { xs: 1, sm: 2 },
                  paddingRight: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                  gap: { xs: 1, sm: 2 },
                  minHeight: { xs: '52px', sm: '64px' },
                },
                '& .MuiTablePagination-selectLabel': {
                  margin: 0,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-select': {
                  marginRight: { xs: 1, sm: 2 },
                  marginLeft: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-actions': {
                  marginLeft: { xs: 0.5, sm: 1 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                },
                '& .MuiIconButton-root': {
                  padding: { xs: '4px', sm: '8px' },
                },
                '& .MuiTablePagination-input': {
                  marginRight: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
                '& .MuiTablePagination-menuItem': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
              }}
            />
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              <DropdownItem
                onClick={() => {
                  handleModalOpen();
                  handleMenuClose();
                }}
              >
                View Details
              </DropdownItem>
            </Menu>
          </Box>
        </CardContent>
      </Card>

      <Modal open={modalOpen} onClose={handleModalClose}>
        <Paper
          elevation={24}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'min(92vw, 540px)',
            maxHeight: '88vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
          }}
        >
          {selectedShipment && (
            <>
              {/* Header: branded bar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2.5,
                  py: 1.75,
                  backgroundColor: colors.swiftcarry[100],
                  color: '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h6" fontWeight={700} color="inherit">
                    Shipment #{selectedShipment.shipmentId}
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedShipment.status)}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontWeight: 600,
                      height: 26,
                      color: '#fff',
                      borderColor: 'rgba(255,255,255,0.7)',
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      '& .MuiChip-label': { color: '#fff' },
                    }}
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={handleModalClose}
                  sx={{
                    color: '#fff',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                  }}
                  aria-label="Close"
                >
                  <Close />
                </IconButton>
              </Box>

              {/* Content - uses row data from fetchShipments */}
              <Box sx={{ overflowY: 'auto', flex: 1, p: 2.5 }}>
                <Grid container spacing={2}>
                  {/* Schedule */}
                  <Grid item xs={12}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                      }}
                    >
                      Schedule
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ mt: 0.75, p: 1.5, borderRadius: 2 }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Expected delivery
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedShipment.exceptedDelivery
                              ? formatToIST(selectedShipment.exceptedDelivery)
                              : '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Created on
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedShipment.createdOn
                              ? formatToIST(selectedShipment.createdOn)
                              : '—'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Location */}
                  <Grid item xs={12}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                      }}
                    >
                      Location
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ mt: 0.75, p: 1.5, borderRadius: 2 }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Source
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedShipment.sourceName ||
                              selectedShipment.source ||
                              '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Destination
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedShipment.destName ||
                              selectedShipment.dest ||
                              '—'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Package & vehicle */}
                  <Grid item xs={12}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                      }}
                    >
                      Package & vehicle
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ mt: 0.75, p: 1.5, borderRadius: 2 }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Package type
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedShipment.pkgTypeName ||
                              selectedShipment.pkgType ||
                              '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Package weight
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedShipment.pkgWeightName || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Dimensions
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedShipment.pkgWidth != null &&
                            selectedShipment.pkgHeight != null
                              ? `${selectedShipment.pkgWidth} × ${selectedShipment.pkgHeight}`
                              : '—'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Created by */}
                  <Grid item xs={12}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                      }}
                    >
                      Created by
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ mt: 0.75, p: 1.5, borderRadius: 2 }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Name
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedShipment.createdBy || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Phone
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {selectedShipment.phoneNo || '—'}
                          </Typography>
                        </Grid>
                        {selectedShipment.createdById != null && (
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              User ID
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {selectedShipment.createdById}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Refund */}
                  <Grid item xs={12}>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                      }}
                    >
                      Refund
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ mt: 0.75, p: 1.5, borderRadius: 2 }}
                    >
                      {selectedShipment.refund &&
                      (selectedShipment.refund.transactionId != null ||
                        selectedShipment.refund.amount != null ||
                        selectedShipment.refund.status != null) ? (
                          <Grid container spacing={1}>
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                              Transaction ID
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {selectedShipment.refund.transactionId ?? '—'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                              Amount
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {selectedShipment.refund.amount != null
                                  ? String(selectedShipment.refund.amount)
                                  : '—'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                              Status
                              </Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {selectedShipment.refund.status ?? '—'}
                              </Typography>
                            </Grid>
                          </Grid>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                          No refund details
                          </Typography>
                        )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Footer */}
              <Box
                sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleModalClose}
                  sx={{
                    py: 1.25,
                    borderRadius: 2,
                    bgcolor: colors.swiftcarry[100],
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: colors.swiftcarry[100],
                      opacity: 0.9,
                    },
                  }}
                >
                  Close
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Modal>
    </>
  );
};

export default ShipmentManagement;
